import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import SignaturePad from "@/components/SignaturePad";
import { BACKEND_URL } from "@/lib/api";
import { CheckCircle2 } from "lucide-react";

const publicApi = axios.create({ baseURL: `${BACKEND_URL}/api` });

export default function PublicSignatureV2() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [role, setRole] = useState("");
  const [comments, setComments] = useState("");
  const [sig, setSig] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    publicApi.get(`/public/itp-v2/signature/${token}`)
      .then((r) => { setData(r.data); setName(r.data?.request?.recipient_name || ""); })
      .catch((e) => { toast.error(e.response?.data?.detail || "Link invalid or expired"); })
      .finally(() => setLoading(false));
  }, [token]);

  const submit = async () => {
    if (!name.trim()) { toast.error("Please enter your name"); return; }
    setSaving(true);
    try {
      await publicApi.post(`/public/itp-v2/signature/${token}/submit`, {
        signatory_name: name.trim(), company: company.trim(), role: role.trim(),
        comments: comments.trim(),
        signature_image_b64: sig, signature_method: sig ? "drawn" : "typed",
      });
      setSubmitted(true);
    } catch (e) { toast.error(e.response?.data?.detail || "Submission failed"); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="p-6 text-sm text-slate-500">Loading…</div>;
  if (!data) return <div className="p-6 text-sm text-rose-700" data-testid="public-sig-invalid">This link is not valid or has expired.</div>;
  if (submitted) return (
    <div className="mx-auto mt-16 max-w-md p-6 text-center" data-testid="public-sig-submitted">
      <CheckCircle2 className="mx-auto size-12 text-emerald-600" />
      <div className="mt-2 text-lg font-semibold text-slate-900">Signature submitted</div>
      <div className="mt-1 text-sm text-slate-600">Thank you. Your signature has been recorded on the FlowQA ITP.</div>
    </div>
  );

  const req = data.request;
  const info = data.active_itp;
  return (
    <div className="mx-auto max-w-2xl space-y-4 p-4 sm:p-6" data-testid="public-signature-page">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">FlowQA — Signature Request</h1>
        <div className="mt-1 text-sm text-slate-600">Please review the row and provide your signature.</div>
      </div>
      <Card className="p-4">
        <div className="text-xs text-slate-500">Project · Lot · ITP</div>
        <div className="text-sm font-semibold text-slate-900" data-testid="public-sig-context">{info.project_name} · {info.lot_number} · {info.template_name} (Rev {info.template_revision})</div>
        <div className="mt-2 text-xs text-slate-500">Signature role</div>
        <div className="text-sm font-semibold text-slate-900" data-testid="public-sig-role">{req.sig_role}</div>
        <div className="mt-2 text-xs text-slate-500">Activity</div>
        <div className="text-sm text-slate-800" data-testid="public-sig-activity">{req.activity || "—"}</div>
        {req.acceptance_criteria && (
          <>
            <div className="mt-2 text-xs text-slate-500">Acceptance criteria</div>
            <div className="text-sm text-slate-800" data-testid="public-sig-criteria">{req.acceptance_criteria}</div>
          </>
        )}
        {req.message && (
          <>
            <div className="mt-2 text-xs text-slate-500">Message</div>
            <div className="text-sm text-slate-800">{req.message}</div>
          </>
        )}
        {(data.evidence || []).length > 0 && (
          <div className="mt-2">
            <div className="text-xs text-slate-500">Evidence ({data.evidence.length})</div>
            <ul className="mt-1 list-disc pl-5 text-xs text-slate-700">
              {data.evidence.map((e) => <li key={e.id}>{e.filename || e.original_name || e.id} · {e.category || "—"}</li>)}
            </ul>
          </div>
        )}
      </Card>
      <Card className="p-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div><Label>Your Name *</Label><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="public-sig-name-input" /></div>
          <div><Label>Company</Label><Input value={company} onChange={(e) => setCompany(e.target.value)} data-testid="public-sig-company-input" /></div>
          <div><Label>Role / Title</Label><Input value={role} onChange={(e) => setRole(e.target.value)} data-testid="public-sig-role-input" /></div>
          <div className="sm:col-span-2"><Label>Comments</Label><Textarea rows={2} value={comments} onChange={(e) => setComments(e.target.value)} data-testid="public-sig-comments-input" /></div>
        </div>
        <div className="mt-3">
          <Label>Signature</Label>
          <SignaturePad value={sig} onChange={setSig} />
          <div className="mt-1 text-[10px] text-slate-500">Draw with mouse, stylus or touch. Optional — a typed sign-off will still be recorded.</div>
        </div>
        <div className="mt-3">
          <Button onClick={submit} disabled={saving || !name.trim()} data-testid="public-sig-submit-btn">Submit Signature</Button>
        </div>
      </Card>
    </div>
  );
}
